import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, DownloadIcon } from 'lucide-react';
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
  }>;
}
const PWAInstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    if (isStandalone) return;
    // Dismissed this session? Don't show again until next visit
    const dismissedThisSession = sessionStorage.getItem('pwa-banner-dismissed');
    if (dismissedThisSession) return;
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(iOS);
    // Show banner after a short delay
    const timer = setTimeout(() => setShowBanner(true), 1500);
    // Listen for the native install prompt (Android/Chrome)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);
  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const {
        outcome
      } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    }
    // On iOS or when no native prompt, the "Install" click just keeps the
    // banner visible so the user can read the iOS hint — they dismiss manually.
  };
  const handleDismiss = () => {
    setShowBanner(false);
    sessionStorage.setItem('pwa-banner-dismissed', 'true');
  };
  return <AnimatePresence data-id="element-2078">
      {showBanner && <motion.div initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: 'auto',
      opacity: 1
    }} exit={{
      height: 0,
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: 'easeInOut'
    }} className="bg-[#00B75F]/[0.06] border-b border-[#00B75F]/15 overflow-hidden" data-id="element-2079">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-2 flex items-center justify-between gap-3" data-id="element-2080">
            <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-2081">
              <DownloadIcon size={14} className="text-[#00B75F] flex-shrink-0" data-id="element-2082" />
              <p className="text-xs text-text-secondary truncate" data-id="element-2083">
                {isIOS ? <>
                    Install Ainstein — tap{' '}
                    <span className="font-semibold text-text-primary" data-id="element-2084">
                      Share ⎋
                    </span>{' '}
                    then{' '}
                    <span className="font-semibold text-text-primary" data-id="element-2085">
                      "Add to Home Screen"
                    </span>
                  </> : <>
                    Get quick access —{' '}
                    <button onClick={handleInstall} className="font-semibold text-[#00B75F] hover:underline underline-offset-2 cursor-pointer" data-id="element-2086">
                      Install Ainstein
                    </button>{' '}
                    on your device
                  </>}
              </p>
            </div>
            <button onClick={handleDismiss} className="p-1 rounded hover:bg-[#00B75F]/10 text-text-tertiary hover:text-text-secondary transition-colors flex-shrink-0" aria-label="Dismiss" data-id="element-2087">
              <XIcon size={14} data-id="element-2088" />
            </button>
          </div>
        </motion.div>}
    </AnimatePresence>;
};
export default PWAInstallPrompt;